import { redirect } from '@tanstack/react-router'

import { useAuthStore } from '@/lib/auth/store'

/**
 * Route guards for `beforeLoad`.
 *
 * They answer one question — is there a session? — and nothing else, with no
 * React Query. Anything that needs the server for more (the onboarding persona
 * check) is composed on top of these by the layout route that owns it.
 *
 * Async, because a tab may not know yet. A reload knows at once: its access
 * token is in sessionStorage. A new tab, or a browser reopened, has to ask the
 * refresh cookie, once (`ensureSession`). The router waits on `beforeLoad`
 * before it renders the route, and index.html's boot splash stays up until the
 * first route resolves, so neither the protected screen nor the sign-in form
 * shows before the answer. After the first answer the guards resolve with no
 * request.
 */

/** The part of beforeLoad's argument a guard reads. */
interface GuardContext {
  location: { href: string }
}

const isAuthenticated = (): Promise<boolean> => useAuthStore.getState().ensureSession()

/**
 * Guards a signed-in route. Sends an anonymous visitor to /signin, carrying
 * where they were going so the sign-in form can put them back there — `href`
 * is app-relative (`/portfolio?tab=holdings`), which is what the `redirect`
 * search param on /signin accepts.
 */
export const requireAuth = async ({ location }: GuardContext): Promise<void> => {
  if (await isAuthenticated()) return
  // A router redirect is a Response, not an Error; throwing it is how
  // beforeLoad navigates. Same on redirectIfAuthenticated below.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/signin', search: { redirect: location.href } })
}

/**
 * Guards the signed-out routes (/signin, /signup). A member who still has a
 * session has no business on them, so send them home rather than let them
 * sign in over themselves.
 */
export const redirectIfAuthenticated = async (): Promise<void> => {
  if (!(await isAuthenticated())) return
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/' })
}
